import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getEffectiveTabs } from '../lib/tabs';
import { Button } from '../components/ui/button';
import { LogOut, Sparkles, ArrowRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Per-tab gradient palette (light, premium)
const TILE_THEMES = {
  dashboard:    { from: 'from-indigo-500',  to: 'to-blue-500',     glow: 'shadow-indigo-500/40',  text: 'text-indigo-50',  ring: 'ring-indigo-300' },
  orders:       { from: 'from-amber-500',   to: 'to-orange-500',   glow: 'shadow-amber-500/40',   text: 'text-amber-50',   ring: 'ring-amber-300' },
  quotations:   { from: 'from-violet-500',  to: 'to-fuchsia-500',  glow: 'shadow-violet-500/40',  text: 'text-violet-50',  ring: 'ring-violet-300' },
  'quotation-requests': { from: 'from-fuchsia-500', to: 'to-pink-600', glow: 'shadow-fuchsia-500/40', text: 'text-fuchsia-50', ring: 'ring-fuchsia-300' },
  timeline:     { from: 'from-rose-500',    to: 'to-pink-500',     glow: 'shadow-rose-500/40',    text: 'text-rose-50',    ring: 'ring-rose-300' },
  'metal-ledger': { from: 'from-yellow-500', to: 'to-amber-600',   glow: 'shadow-yellow-500/40',  text: 'text-yellow-50',  ring: 'ring-yellow-300' },
  chains:       { from: 'from-cyan-500',    to: 'to-teal-500',     glow: 'shadow-cyan-500/40',    text: 'text-cyan-50',    ring: 'ring-cyan-300' },
  factories:    { from: 'from-slate-700',   to: 'to-slate-900',    glow: 'shadow-slate-700/50',   text: 'text-slate-50',   ring: 'ring-slate-400' },
  customers:    { from: 'from-emerald-500', to: 'to-green-600',    glow: 'shadow-emerald-500/40', text: 'text-emerald-50', ring: 'ring-emerald-300' },
  exhibitions:  { from: 'from-amber-500',   to: 'to-yellow-600',   glow: 'shadow-amber-500/40',   text: 'text-amber-50',   ring: 'ring-amber-300' },
  receivables:  { from: 'from-green-600',   to: 'to-teal-700',     glow: 'shadow-green-700/40',   text: 'text-green-50',   ring: 'ring-green-300' },
  'lab-planner': { from: 'from-violet-500', to: 'to-indigo-600',   glow: 'shadow-violet-500/40',  text: 'text-violet-50',  ring: 'ring-violet-300' },
  sales:        { from: 'from-blue-500',    to: 'to-cyan-600',     glow: 'shadow-blue-500/40',    text: 'text-blue-50',    ring: 'ring-blue-300' },
  users:        { from: 'from-purple-500',  to: 'to-indigo-600',   glow: 'shadow-purple-500/40',  text: 'text-purple-50',  ring: 'ring-purple-300' },
  settings:     { from: 'from-neutral-600', to: 'to-neutral-800',  glow: 'shadow-neutral-600/40', text: 'text-neutral-50', ring: 'ring-neutral-400' },
};

const FALLBACK = { from: 'from-slate-500', to: 'to-slate-700', glow: 'shadow-slate-500/40', text: 'text-slate-50', ring: 'ring-slate-300' };

const formatCr = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)} Cr`;
  if (Math.abs(v) >= 1_00_000) return `₹${(v / 1_00_000).toFixed(1)} L`;
  if (Math.abs(v) >= 1_000) return `₹${(v / 1_000).toFixed(0)}k`;
  return `₹${v.toFixed(0)}`;
};

// Returns the live mini-stat string for a tab key, given the stats payload.
const formatStat = (key, stats) => {
  if (!stats) return null;
  switch (key) {
    case 'dashboard':    return stats.orders_active != null ? `${stats.orders_active} active orders` : null;
    case 'orders':       return stats.orders_active != null ? `${stats.orders_active} active` : null;
    case 'quotations':   return stats.quotations_open != null ? `${stats.quotations_open} open` : null;
    case 'quotation-requests': return stats.quotation_requests_count != null ? (stats.quotation_requests_count > 0 ? `${stats.quotation_requests_count} request${stats.quotation_requests_count === 1 ? '' : 's'}` : 'Tap to start') : 'Tap to start';
    case 'timeline':     return stats.timeline_due_week != null ? `${stats.timeline_due_week} due this week` : null;
    case 'metal-ledger': return stats.gold_pending_g != null ? `${stats.gold_pending_g.toFixed(2)}g pending` : null;
    case 'chains':       return stats.chains_in_stock != null ? `${stats.chains_in_stock} in stock` : null;
    case 'factories':    return stats.factories_count != null ? `${stats.factories_count} factories` : null;
    case 'customers':    return stats.customers_count != null ? `${stats.customers_count} customers` : null;
    case 'receivables':  return stats.ar_outstanding != null ? `${formatCr(stats.ar_outstanding)} outstanding` : null;
    case 'lab-planner':  return stats.lab_recs_pending != null ? (stats.lab_recs_pending > 0 ? `${stats.lab_recs_pending} cert action${stats.lab_recs_pending === 1 ? '' : 's'}` : 'All caught up') : null;
    case 'sales':        return null;
    case 'users':        return stats.users_count != null ? `${stats.users_count} team members` : null;
    case 'settings':     return 'Configure';
    default:             return null;
  }
};

const Tile = ({ tab, stat, onLaunch, index }) => {
  const Icon = tab.icon;
  const theme = TILE_THEMES[tab.key] || FALLBACK;
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);

  const handleClick = () => {
    if (clicked) return;
    setClicked(true);
    // Trigger zoom animation, then navigate
    setTimeout(() => onLaunch(tab.path), 380);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`menu-tile-${tab.key}`}
      style={{
        animationDelay: `${index * 60}ms`,
      }}
      className={cn(
        "group relative overflow-hidden text-left rounded-2xl md:rounded-3xl bg-gradient-to-br",
        theme.from, theme.to,
        "p-3 md:p-7 shadow-md md:shadow-lg transition-all duration-500 will-change-transform",
        "tile-enter",
        hovered && !clicked && `md:scale-[1.04] md:-translate-y-1 md:shadow-2xl ${theme.glow}`,
        clicked && "scale-[1.15] opacity-0 blur-md",
        "focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-offset-transparent",
        theme.ring,
      )}
    >
      {/* Decorative glow blob */}
      <div className={cn(
        "absolute -top-12 -right-12 h-44 w-44 rounded-full bg-white/20 blur-3xl transition-all duration-500",
        hovered && "scale-150 bg-white/30"
      )} />
      <div className={cn(
        "absolute -bottom-16 -left-10 h-40 w-40 rounded-full bg-black/10 blur-3xl transition-opacity duration-500",
        hovered ? "opacity-100" : "opacity-50"
      )} />

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full min-h-[110px] md:min-h-[180px]">
        <div className={cn(
          "h-9 w-9 md:h-14 md:w-14 rounded-xl md:rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-2 md:mb-4 transition-all duration-500",
          hovered && "md:rotate-6 md:scale-110 bg-white/30"
        )}>
          <Icon className={cn("h-5 w-5 md:h-7 md:w-7", theme.text)} strokeWidth={2.2} />
        </div>

        <h3 className={cn("font-heading text-sm md:text-2xl font-bold tracking-tight leading-tight line-clamp-2", theme.text)}>
          {tab.label}
        </h3>
        {tab.description && (
          <p className={cn("hidden md:block mt-1 text-sm font-medium opacity-85", theme.text)}>
            {tab.description}
          </p>
        )}

        <div className="mt-auto pt-2 md:pt-4 flex items-center justify-between gap-1">
          {stat ? (
            <span className={cn(
              "inline-flex items-center text-[10px] md:text-sm font-semibold px-1.5 md:px-2.5 py-0.5 md:py-1 rounded-full bg-white/20 backdrop-blur-sm whitespace-nowrap truncate",
              theme.text
            )}>
              {stat}
            </span>
          ) : (
            <span />
          )}
          <ArrowRight
            className={cn(
              "hidden md:block h-5 w-5 transition-all duration-500 flex-shrink-0",
              theme.text,
              hovered ? "translate-x-1 opacity-100" : "opacity-60"
            )}
          />
        </div>
      </div>
    </button>
  );
};

const MainMenu = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme: appTheme, toggleTheme } = useTheme();
  const [stats, setStats] = useState(null);
  const [navigatingTo, setNavigatingTo] = useState(null);

  useEffect(() => {
    let active = true;
    axios.get(`${API_URL}/menu/tile-stats`)
      .then(res => { if (active) setStats(res.data); })
      .catch(() => { /* tiles still render without stats */ });
    return () => { active = false; };
  }, []);

  const handleLaunch = (path) => {
    setNavigatingTo(path);
    navigate(path);
  };

  const tiles = getEffectiveTabs(user);
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();
  const firstName = (user?.name || '').split(' ')[0] || 'there';

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-amber-50/40 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950">
      {/* Ambient background blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-amber-300/30 dark:bg-amber-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -right-32 h-[480px] w-[480px] rounded-full bg-indigo-300/30 dark:bg-indigo-500/10 blur-3xl" />
        <div className="absolute top-1/3 left-1/3 h-72 w-72 rounded-full bg-rose-200/30 dark:bg-rose-500/10 blur-3xl" />
      </div>

      {/* Top bar */}
      <header className="relative z-20 px-3 md:px-10 py-4 md:py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/40">
            <Sparkles className="h-4.5 w-4.5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-heading text-lg md:text-xl font-bold text-slate-900 dark:text-white tracking-tight">
            Siddhi Jewels
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleTheme}
            className="h-9 hidden sm:flex"
            data-testid="menu-theme-toggle"
          >
            {appTheme === 'dark' ? 'Light' : 'Dark'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { logout(); navigate('/login'); }}
            className="h-9 text-rose-600 border-rose-200 hover:bg-rose-50 dark:text-rose-400 dark:border-rose-900 dark:hover:bg-rose-950/40"
            data-testid="menu-logout-btn"
          >
            <LogOut className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>
      </header>

      {/* Hero */}
      <div className="relative z-10 px-3 md:px-10 pb-4 md:pb-6">
        <p className="text-[10px] md:text-sm uppercase tracking-[0.2em] text-amber-600 dark:text-amber-400 font-semibold mb-1">
          {greeting}
        </p>
        <h1 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white leading-tight">
          Hi {firstName}, what would you like to do today?
        </h1>
        <p className="hidden sm:block mt-2 text-sm md:text-base text-slate-600 dark:text-slate-400 max-w-2xl">
          Pick a module below to get started. Live counts are pulled from your production data right now.
        </p>
      </div>

      {/* Tiles grid */}
      <main className="relative z-10 px-3 md:px-10 pb-16">
        {tiles.length === 0 ? (
          <div className="text-center py-24 text-slate-500 dark:text-slate-400">
            No modules available for your role. Contact an admin.
          </div>
        ) : (
          <div
            className={cn(
              "grid gap-2.5 md:gap-5",
              "grid-cols-3 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4"
            )}
            data-testid="main-menu-tiles"
          >
            {tiles.map((tab, i) => (
              <Tile
                key={tab.key}
                tab={tab}
                stat={formatStat(tab.key, stats)}
                onLaunch={handleLaunch}
                index={i}
              />
            ))}
          </div>
        )}
      </main>

      {/* Loading overlay during navigation */}
      {navigatingTo && (
        <div className="fixed inset-0 z-50 bg-white/70 dark:bg-neutral-950/70 backdrop-blur-md flex items-center justify-center menu-fade-in pointer-events-none">
          <div className="h-12 w-12 rounded-full border-4 border-amber-400 border-t-transparent animate-spin" />
        </div>
      )}

      {/* Inline styles for entrance animation */}
      <style>{`
        @keyframes tile-enter {
          0% { opacity: 0; transform: translateY(16px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .tile-enter { animation: tile-enter 520ms cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        @keyframes menu-fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        .menu-fade-in { animation: menu-fade-in 280ms ease-out forwards; }
      `}</style>
    </div>
  );
};

export default MainMenu;
